import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BonusAttributeField, type LootboxBonus } from '@/api/common';
import { DEFAULT_CURRENCIES } from '@/helpers/currency';
import { convertMaxWin, formatLootboxItemTitle } from '../helpers/fortuneWheel';

export const useLootboxBonus = (bonus: LootboxBonus | null) => {
  const { t } = useTranslation('common');

  const wager = useMemo(() => {
    if (bonus?.type === 'freespins') {
      return bonus?.resultBonus!.find(
        (item) => item.field === BonusAttributeField.Wager
      )!.value;
    }

    return bonus?.attributes!.find(
      (item) => item.field === BonusAttributeField.Wager
    )!.value;
  }, [bonus]);

  const freespinsCount = useMemo(() => {
    if (bonus?.type !== 'freespins') return null;

    const value = bonus?.attributes.find(
      (item) => item.field === BonusAttributeField.FreespinsCount
    )!.value;

    if (typeof value !== 'number' || !value) return null;

    return t('amountShortFS', { amount: value });
  }, [bonus, t]);

  const bonusAmount = useMemo(() => {
    if (bonus?.type === 'freespins') return null;

    const value = bonus?.attributes!.find(
      (item) => item.field === BonusAttributeField.BonusAmount
    )!.value;

    if (!Array.isArray(value)) return null;

    return convertMaxWin(value, DEFAULT_CURRENCIES);
  }, [bonus]);

  const maxWin = useMemo(() => {
    if (bonus?.type !== 'freespins') return null;

    const value = bonus?.resultBonus!.find(
      (item) => item.field === BonusAttributeField.MaxWin
    )!.value;

    if (!Array.isArray(value)) return null;

    return convertMaxWin(value, DEFAULT_CURRENCIES);
  }, [bonus]);

  const title = useMemo(
    () => formatLootboxItemTitle(bonus?.title ?? ''),
    [bonus?.title]
  );

  return { wager, bonusAmount, maxWin, title, freespinsCount };
};
